import { memoize } from 'micro-memoize';

import { DispatchedReportItem, ExportedReportItem, ReportScheduleItem } from '@futrli-sdk/report_scheduling';
import { WorkspaceMember } from '@futrli-sdk/people';

import { ApiView, ApiViewState, getDefaultApiViewState } from './core/ApiView';
import {
  createScheduleReportMutation,
  CreateScheduleInput,
  updateScheduleReportMutation,
  UpdateScheduleInput,
  deleteScheduledReportMutation,
  DeleteScheduleInput,
  approveDispatchedReportMutation,
  ApproveDispatchedInput,
  denyDispatchedReportMutation,
  DenyDispatchedInput,
  deleteDispatchedReportMutation,
  DeleteDispatchedInput,
  deleteExportedReportMutation,
  DeleteExportedInput
} from './mutations/reportScheduleActions';
import { awaitingReviewReportsQuery } from './queries/reportSchedules';
import { dispatchedReportsQuery } from './queries/reportSchedules';
import { exportedReportsQuery } from './queries/reportSchedules';
import { membersQuery } from './queries/members';
import { combineExportedPdfItems, ExportedPdfListItem } from './processors/reportScheduling/exportedPdfList';
import { deriveScheduleList, ScheduleListItem } from './processors/reportScheduling/reportSchedulingList';
import { reportSchedulesQuery } from './queries/reportSchedules';

export interface ReportSchedulingViewState extends ApiViewState {
  members: WorkspaceMember[];
  scheduledReports: ReportScheduleItem[];
  scheduleItems: ScheduleListItem[];
  awaitingReviewReports: DispatchedReportItem[];
  dispatchedReports: DispatchedReportItem[];
  exportedReports: ExportedReportItem[];
  exportedPdfItems: ExportedPdfListItem[];
  isCreatingSchedule: boolean;
  isUpdatingSchedule: boolean;
  isDeletingSchedule: boolean;
  isApprovingDispatched: boolean;
  isDenyingDispatched: boolean;
  isDeletingDispatched: boolean;
  isDeletingScheduleItem: boolean;
  isDeletingExported: boolean;
  isFetchingAwaitingReview: boolean;
  errorCreatingSchedule: Error | null;
  errorUpdatingSchedule: Error | null;
  errorDeletingSchedule: Error | null;
  errorApprovingDispatched: Error | null;
  errorDenyingDispatched: Error | null;
  errorDeletingDispatched: Error | null;
  errorDeletingScheduleItem: Error | null;
  errorDeletingExported: Error | null;
}

export interface ReportSchedulingViewQueryOptions {
  schedules?: boolean;
  awaitingReview?: boolean;
  dispatched?: boolean;
  exported?: boolean;
  members?: boolean;
}

export class ReportSchedulingView extends ApiView {
  private scheduledReportsObs: ReturnType<typeof reportSchedulesQuery>;

  private awaitingReviewReportsObs: ReturnType<typeof awaitingReviewReportsQuery>;

  private dispatchedReportsObs: ReturnType<typeof dispatchedReportsQuery>;

  private exportedReportsObs: ReturnType<typeof exportedReportsQuery>;

  private membersObs: ReturnType<typeof membersQuery>;

  private createScheduleMutation: ReturnType<typeof createScheduleReportMutation>;

  private updateScheduleMutation: ReturnType<typeof updateScheduleReportMutation>;

  private deleteScheduleMutation: ReturnType<typeof deleteScheduledReportMutation>;

  private approveDispatchedMutation: ReturnType<typeof approveDispatchedReportMutation>;

  private denyDispatchedMutation: ReturnType<typeof denyDispatchedReportMutation>;

  private deleteDispatchedMutation: ReturnType<typeof deleteDispatchedReportMutation>;

  private deleteExportedMutation: ReturnType<typeof deleteExportedReportMutation>;

  private memoizedDeriveScheduleList: typeof deriveScheduleList;

  private memoizedCombineExportedPdfItems: typeof combineExportedPdfItems;

  public state: ReportSchedulingViewState = {
    ...getDefaultApiViewState(),
    members: [],
    scheduledReports: [],
    scheduleItems: [],
    awaitingReviewReports: [],
    dispatchedReports: [],
    exportedReports: [],
    exportedPdfItems: [],
    isCreatingSchedule: false,
    isUpdatingSchedule: false,
    isDeletingSchedule: false,
    isApprovingDispatched: false,
    isDenyingDispatched: false,
    isDeletingDispatched: false,
    isDeletingScheduleItem: false,
    isDeletingExported: false,
    isFetchingAwaitingReview: false,
    errorCreatingSchedule: null,
    errorUpdatingSchedule: null,
    errorDeletingSchedule: null,
    errorApprovingDispatched: null,
    errorDenyingDispatched: null,
    errorDeletingDispatched: null,
    errorDeletingScheduleItem: null,
    errorDeletingExported: null
  };

  constructor(
    private workspaceId: string,
    private organisationId: string,
    private isConsolidation: boolean,
    private queryOptions: ReportSchedulingViewQueryOptions = {}
  ) {
    super();

    const {
      schedules: enableSchedules = true,
      awaitingReview: enableAwaitingReview = true,
      dispatched: enableDispatched = false,
      exported: enableExported = true,
      members: enableMembers = false
    } = queryOptions;

    // Observers
    this.scheduledReportsObs = this.addQuery(
      reportSchedulesQuery(workspaceId, organisationId, isConsolidation, { enabled: enableSchedules })
    );

    this.awaitingReviewReportsObs = this.addQuery(
      awaitingReviewReportsQuery(workspaceId, organisationId, isConsolidation, { enabled: enableAwaitingReview })
    );

    this.dispatchedReportsObs = this.addQuery(
      dispatchedReportsQuery(workspaceId, organisationId, isConsolidation, { enabled: enableDispatched })
    );

    this.exportedReportsObs = this.addQuery(
      exportedReportsQuery(workspaceId, organisationId, isConsolidation, { enabled: enableExported })
    );

    this.membersObs = this.addQuery(
      membersQuery(workspaceId, organisationId, isConsolidation, {
        enabled: enableMembers
      })
    );

    // Mutations
    this.createScheduleMutation = this.addMutation(
      createScheduleReportMutation(workspaceId, organisationId, isConsolidation)
    );

    this.updateScheduleMutation = this.addMutation(
      updateScheduleReportMutation(workspaceId, organisationId, isConsolidation)
    );

    this.deleteScheduleMutation = this.addMutation(
      deleteScheduledReportMutation(workspaceId, organisationId, isConsolidation)
    );

    this.approveDispatchedMutation = this.addMutation(
      approveDispatchedReportMutation(workspaceId, organisationId, isConsolidation)
    );

    this.denyDispatchedMutation = this.addMutation(
      denyDispatchedReportMutation(workspaceId, organisationId, isConsolidation)
    );

    this.deleteDispatchedMutation = this.addMutation(
      deleteDispatchedReportMutation(workspaceId, organisationId, isConsolidation)
    );

    this.deleteExportedMutation = this.addMutation(
      deleteExportedReportMutation(workspaceId, organisationId, isConsolidation)
    );

    this.memoizedDeriveScheduleList = memoize(deriveScheduleList);
    this.memoizedCombineExportedPdfItems = memoize(combineExportedPdfItems);

    this.updateState();
  }

  // ***************
  // *  Public API *
  // ***************
  public createSchedule(data: CreateScheduleInput) {
    return this.createScheduleMutation.mutate(data);
  }

  public updateSchedule(data: UpdateScheduleInput) {
    return this.updateScheduleMutation.mutate(data);
  }

  public deleteSchedule(data: DeleteScheduleInput) {
    return this.deleteScheduleMutation.mutate(data);
  }

  public deleteScheduleItem(item: ScheduleListItem) {
    if (item.source === 'dispatched') {
      return this.deleteDispatched({ dispatchedReportId: item.sourceItem.id });
    }

    return this.deleteSchedule({ scheduleReportId: item.sourceItem.id });
  }

  public approveDispatched(data: ApproveDispatchedInput) {
    return this.approveDispatchedMutation.mutate(data);
  }

  public denyDispatched(data: DenyDispatchedInput) {
    return this.denyDispatchedMutation.mutate(data);
  }

  public deleteDispatched(data: DeleteDispatchedInput) {
    return this.deleteDispatchedMutation.mutate(data);
  }

  public deleteExported(data: DeleteExportedInput) {
    return this.deleteExportedMutation.mutate(data);
  }

  // ****************************
  // *  Private / Protected API *
  // ****************************
  protected getNewState(): ReportSchedulingViewState {
    const members = this.getResult(this.membersObs).data ?? [];
    const scheduledReports = this.getResult(this.scheduledReportsObs).data ?? [];
    const awaitingReviewReports = this.getResult(this.awaitingReviewReportsObs).data ?? [];
    const dispatchedReports = this.getResult(this.dispatchedReportsObs).data ?? [];
    const exportedReports = this.getResult(this.exportedReportsObs).data ?? [];

    return {
      ...super.getNewState(),
      members,
      scheduledReports,
      scheduleItems: this.memoizedDeriveScheduleList(scheduledReports, awaitingReviewReports, members),
      awaitingReviewReports,
      dispatchedReports,
      exportedReports,
      exportedPdfItems: this.memoizedCombineExportedPdfItems(exportedReports, dispatchedReports),
      isCreatingSchedule: this.createScheduleMutation.getCurrentResult().status === 'pending',
      isUpdatingSchedule: this.updateScheduleMutation.getCurrentResult().status === 'pending',
      isDeletingSchedule: this.deleteScheduleMutation.getCurrentResult().status === 'pending',
      isApprovingDispatched: this.approveDispatchedMutation.getCurrentResult().status === 'pending',
      isDenyingDispatched: this.denyDispatchedMutation.getCurrentResult().status === 'pending',
      isDeletingDispatched: this.deleteDispatchedMutation.getCurrentResult().status === 'pending',
      isDeletingScheduleItem:
        this.deleteScheduleMutation.getCurrentResult().status === 'pending' ||
        this.deleteDispatchedMutation.getCurrentResult().status === 'pending',
      isDeletingExported: this.deleteExportedMutation.getCurrentResult().status === 'pending',
      isFetchingAwaitingReview: this.getResult(this.awaitingReviewReportsObs).isFetching,
      errorCreatingSchedule: this.createScheduleMutation.getCurrentResult().error,
      errorUpdatingSchedule: this.updateScheduleMutation.getCurrentResult().error,
      errorDeletingSchedule: this.deleteScheduleMutation.getCurrentResult().error,
      errorApprovingDispatched: this.approveDispatchedMutation.getCurrentResult().error,
      errorDenyingDispatched: this.denyDispatchedMutation.getCurrentResult().error,
      errorDeletingDispatched: this.deleteDispatchedMutation.getCurrentResult().error,
      errorDeletingScheduleItem:
        this.deleteScheduleMutation.getCurrentResult().error ?? this.deleteDispatchedMutation.getCurrentResult().error,
      errorDeletingExported: this.deleteExportedMutation.getCurrentResult().error
    };
  }
}
